import { useState } from 'react';

import type { NoteKind } from '../../../../api/venue-marketing';

import { FormAlert } from './FormAlert';
import { NOTE_KIND_LABELS } from './marketing-note-lists';
import { NoteTextForm } from './NoteTextForm';

interface NoteComposerProps {
  /** The kinds on offer. One kind hides the picker. */
  kinds: NoteKind[];
  label: string;
  placeholder: string;
  busy: boolean;
  /** Resolves to an error message, or null once the note is saved. */
  onAdd: (kind: NoteKind, body: string) => Promise<string | null>;
  onClose: () => void;
}

/** Writes a new note or action item. Stays open, text kept, if the save fails. */
export function NoteComposer({
  kinds,
  label,
  placeholder,
  busy,
  onAdd,
  onClose,
}: NoteComposerProps) {
  const [kind, setKind] = useState<NoteKind>(kinds[0]);
  const [error, setError] = useState<string | null>(null);

  const submit = async (body: string) => {
    setError(null);
    const failure = await onAdd(kind, body);
    if (failure) setError(failure);
    else onClose();
  };

  return (
    <div
      style={{
        border: '1px solid var(--nb-border)',
        borderRadius: 10,
        padding: 10,
        background: 'var(--nb-surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {kinds.length > 1 && (
        <div role="group" aria-label="Note type" style={{ display: 'flex', gap: 4 }}>
          {kinds.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              onClick={() => setKind(k)}
              style={{
                height: 24,
                padding: '0 10px',
                borderRadius: 999,
                border: '1px solid',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                borderColor: kind === k ? 'var(--nb-primary)' : 'var(--nb-border)',
                background: kind === k ? 'var(--nb-primary-soft)' : 'transparent',
                color: kind === k ? 'var(--nb-primary)' : 'var(--nb-muted)',
              }}
            >
              {NOTE_KIND_LABELS[k]}
            </button>
          ))}
        </div>
      )}
      <NoteTextForm
        initial=""
        label={label}
        placeholder={placeholder}
        submitLabel="Add"
        busy={busy}
        onCancel={onClose}
        onSubmit={(body) => void submit(body)}
        autoFocus
      />
      {error && <FormAlert message={error} />}
    </div>
  );
}
